import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonSkeletonText,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PosStoreService } from '@adminpages/pos-state';
import { PosCashMovement, PosCashSession } from '@adminpages/pos-api';

@Component({
  selector: 'foco-pos-cash-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonButton,
    IonButtons,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonSkeletonText,
    IonText,
    IonTitle,
    IonToolbar,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button [routerLink]="returnRoute()">
            <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>{{ store.selectedCashRegister()?.name || 'Caja' }}</ion-title>
        <ion-buttons slot="end">
          @if (store.cashRegisters().length > 1) {
            <ion-button routerLink="/cash-register" aria-label="Cambiar caja" title="Cambiar caja">
              <ion-icon name="swap-horizontal-outline" slot="icon-only"></ion-icon>
            </ion-button>
          }
          <ion-button (click)="openHistoryModal()">
            <ion-icon name="time-outline" slot="start"></ion-icon>
            Historial
          </ion-button>
          @if (store.cashScope() === 'pos') {
            <ion-button routerLink="/pos/recent">
              <ion-icon name="receipt-outline" slot="start"></ion-icon>
              Ventas
            </ion-button>
          }
          @if (store.cashSession()) {
            <ion-button (click)="movementOpen.set(true)">
              <ion-icon name="add" slot="start"></ion-icon>
              Movimiento
            </ion-button>
            <ion-button color="danger" (click)="openClosingModal()">
              Cerrar
            </ion-button>
          }
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="pos-page cash-content">
      <main class="cash-shell">
        @if (store.errorMessage(); as error) {
          <section class="cash-message error">{{ error }}</section>
        }

        @if (!cashReady()) {
          <section class="summary-grid">
            @for (item of [1, 2, 3, 4]; track item) {
              <article class="skeleton-card">
                <ion-skeleton-text animated style="width: 40%"></ion-skeleton-text>
                <ion-skeleton-text animated style="width: 70%; height: 26px"></ion-skeleton-text>
              </article>
            }
          </section>

          <section class="cash-card movements-card">
            <header>
              <ion-skeleton-text animated style="width: 120px"></ion-skeleton-text>
              <ion-skeleton-text animated style="width: 220px; height: 28px"></ion-skeleton-text>
            </header>
            @for (item of [1, 2, 3, 4]; track item) {
              <article class="movement-row skeleton-row">
                <div>
                  <ion-skeleton-text animated style="width: 72%"></ion-skeleton-text>
                  <ion-skeleton-text animated style="width: 48%"></ion-skeleton-text>
                </div>
                <ion-skeleton-text animated style="width: 88px; height: 22px"></ion-skeleton-text>
              </article>
            }
          </section>
        } @else if (!store.cashSession()) {
          <section class="cash-card open-card">
            <header>
              <span>Caja cerrada</span>
              <h1>Abrir caja</h1>
            </header>

            <ion-item>
              <ion-label position="stacked">Fondo inicial efectivo</ion-label>
              <ion-input type="number" [ngModel]="openingCashAmount()" (ngModelChange)="openingCashAmount.set(+$event)"></ion-input>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Notas</ion-label>
              <ion-input [ngModel]="openingNotes()" (ngModelChange)="openingNotes.set($event)"></ion-input>
            </ion-item>

            <ion-button expand="block" size="large" [disabled]="store.cashLoading()" (click)="openCash()">
              {{ store.cashLoading() ? 'Abriendo...' : 'Abrir caja' }}
            </ion-button>
          </section>
        } @else {
          @if (store.cashSession(); as session) {
            <section class="summary-grid">
              <article>
                <span>Folio</span>
                <strong>{{ session.folio }}</strong>
              </article>
              <article>
                <span>Efectivo esperado</span>
                <strong>{{ store.money(session.expectedCashAmount) }}</strong>
              </article>
              <article>
                <span>Transferencias</span>
                <strong>{{ store.money(session.expectedTransferAmount) }}</strong>
              </article>
              <article>
                <span>Total caja</span>
                <strong>{{ store.money(session.expectedTotal) }}</strong>
              </article>
            </section>

            <section class="cash-card movements-card">
              <header>
                <span>Movimientos</span>
                <h2>Actividad de caja</h2>
              </header>

              @for (movement of session.movements; track movement.uuid) {
                <article class="movement-row">
                  <div>
                    <strong>{{ movement.description || movementLabel(movement.type) }}</strong>
                    <small>{{ movementLabel(movement.type) }} · {{ methodLabel(movement.method) }}</small>
                  </div>
                  <strong [class.negative]="movement.amount < 0">{{ store.money(movement.amount) }}</strong>
                </article>
              } @empty {
                <ion-text color="medium">Sin movimientos todavia.</ion-text>
              }
            </section>
          }
        }
      </main>
    </ion-content>

    @if (cashReady() && store.cashSession()) {
      <ion-modal [isOpen]="movementOpen()" (didDismiss)="movementOpen.set(false)">
        <ng-template>
          <ion-header>
            <ion-toolbar>
              <ion-title>Registrar movimiento</ion-title>
              <ion-buttons slot="end">
                <ion-button (click)="movementOpen.set(false)">
                  <ion-icon name="close" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-buttons>
            </ion-toolbar>
          </ion-header>

          <ion-content class="cash-modal-content">
            <ion-item>
              <ion-label position="stacked">Tipo</ion-label>
              <ion-select [ngModel]="movementType()" (ngModelChange)="movementType.set($event)">
                <ion-select-option value="expense">Gasto</ion-select-option>
                <ion-select-option value="withdrawal">Retiro</ion-select-option>
                <ion-select-option value="cash_in">Entrada</ion-select-option>
                <ion-select-option value="adjustment">Ajuste</ion-select-option>
              </ion-select>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Metodo</ion-label>
              <ion-select [ngModel]="movementMethod()" (ngModelChange)="movementMethod.set($event)">
                <ion-select-option value="cash">Efectivo</ion-select-option>
                <ion-select-option value="transfer">Transferencia</ion-select-option>
              </ion-select>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Monto</ion-label>
              <ion-input type="number" [ngModel]="movementAmount()" (ngModelChange)="movementAmount.set(+$event)"></ion-input>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Descripcion</ion-label>
              <ion-input [ngModel]="movementDescription()" (ngModelChange)="movementDescription.set($event)"></ion-input>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Referencia</ion-label>
              <ion-input [ngModel]="movementReference()" (ngModelChange)="movementReference.set($event)"></ion-input>
            </ion-item>

            @if (store.errorMessage(); as error) {
              <ion-text color="danger">{{ error }}</ion-text>
            }
          </ion-content>

          <ion-footer>
            <ion-toolbar>
              <ion-button expand="block" size="large" [disabled]="store.cashLoading()" (click)="addMovement()">
                {{ store.cashLoading() ? 'Guardando...' : 'Guardar movimiento' }}
              </ion-button>
            </ion-toolbar>
          </ion-footer>
        </ng-template>
      </ion-modal>

      <ion-modal [isOpen]="closingOpen()" (didDismiss)="closingOpen.set(false)">
        <ng-template>
          <ion-header>
            <ion-toolbar>
              <ion-title>Cerrar caja</ion-title>
              <ion-buttons slot="end">
                <ion-button (click)="closingOpen.set(false)">
                  <ion-icon name="close" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-buttons>
            </ion-toolbar>
          </ion-header>

          <ion-content class="cash-modal-content">
            @if (store.cashSession(); as session) {
              <section class="closing-summary">
                <article>
                  <span>Efectivo esperado</span>
                  <strong>{{ store.money(session.expectedCashAmount) }}</strong>
                </article>
                <article>
                  <span>Transferencias</span>
                  <strong>{{ store.money(session.expectedTransferAmount) }}</strong>
                </article>
              </section>
            }

            <ion-item>
              <ion-label position="stacked">Efectivo contado</ion-label>
              <ion-input type="number" [ngModel]="countedCashAmount()" (ngModelChange)="countedCashAmount.set(+$event)"></ion-input>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Transferencias contadas</ion-label>
              <ion-input type="number" [ngModel]="countedTransferAmount()" (ngModelChange)="countedTransferAmount.set(+$event)"></ion-input>
            </ion-item>

            <ion-item>
              <ion-label position="stacked">Notas cierre</ion-label>
              <ion-input [ngModel]="closingNotes()" (ngModelChange)="closingNotes.set($event)"></ion-input>
            </ion-item>

            @if (store.errorMessage(); as error) {
              <ion-text color="danger">{{ error }}</ion-text>
            }
          </ion-content>

          <ion-footer>
            <ion-toolbar>
              <ion-button color="danger" expand="block" size="large" [disabled]="store.cashLoading()" (click)="closeCash()">
                {{ store.cashLoading() ? 'Cerrando...' : 'Cerrar caja' }}
              </ion-button>
            </ion-toolbar>
          </ion-footer>
        </ng-template>
      </ion-modal>
    }

    <ion-modal [isOpen]="historyOpen()" (didDismiss)="historyOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Historial de cierres</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="openHistoryModal()">
                <ion-icon name="refresh" slot="icon-only"></ion-icon>
              </ion-button>
              <ion-button (click)="historyOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="cash-modal-content">
          @if (store.cashHistoryLoading()) {
            <section class="history-list">
              @for (item of [1, 2, 3]; track item) {
                <article class="history-card">
                  <ion-skeleton-text animated style="width: 44%"></ion-skeleton-text>
                  <ion-skeleton-text animated style="width: 70%; height: 28px"></ion-skeleton-text>
                  <ion-skeleton-text animated style="width: 88%"></ion-skeleton-text>
                </article>
              }
            </section>
          } @else {
            <section class="history-list">
              @for (session of store.cashHistory(); track session.uuid) {
                <article class="history-card" (click)="openHistoryDetail(session)">
                  <header>
                    <div>
                      <span>{{ session.closedAt || session.openedAt }}</span>
                      <strong>{{ session.folio }}</strong>
                    </div>
                    <span class="difference-pill" [class.positive]="session.differenceAmount > 0" [class.negative]="session.differenceAmount < 0">
                      {{ store.money(session.differenceAmount) }}
                    </span>
                  </header>

                  <div class="history-totals">
                    <div>
                      <span>Esperado</span>
                      <strong>{{ store.money(session.expectedTotal) }}</strong>
                    </div>
                    <div>
                      <span>Contado</span>
                      <strong>{{ store.money(countedTotal(session)) }}</strong>
                    </div>
                    <div>
                      <span>Movimientos</span>
                      <strong>{{ session.movements.length }}</strong>
                    </div>
                  </div>

                  <small>
                    Abrió {{ session.openedByUser || 'N/D' }} · Cerró {{ session.closedByUser || 'N/D' }}
                  </small>
                </article>
              } @empty {
                <section class="cash-message">Todavia no hay cierres de caja registrados.</section>
              }
            </section>
          }
        </ion-content>
      </ng-template>
    </ion-modal>

    <ion-modal [isOpen]="historyDetailOpen()" (didDismiss)="historyDetailOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Detalle de cierre</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="historyDetailOpen.set(false)">
                <ion-icon name="close" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <ion-content class="cash-modal-content">
          @if (selectedHistorySession(); as session) {
            <section class="closing-detail-hero">
              <span>{{ session.status === 'closed' ? 'Caja cerrada' : 'Caja abierta' }}</span>
              <h2>{{ session.folio }}</h2>
              <strong>{{ store.money(session.differenceAmount) }}</strong>
              <small>Diferencia</small>
            </section>

            <section class="closing-summary">
              <article>
                <span>Efectivo esperado</span>
                <strong>{{ store.money(session.expectedCashAmount) }}</strong>
              </article>
              <article>
                <span>Efectivo contado</span>
                <strong>{{ store.money(session.countedCashAmount || 0) }}</strong>
              </article>
              <article>
                <span>Transfer esperado</span>
                <strong>{{ store.money(session.expectedTransferAmount) }}</strong>
              </article>
              <article>
                <span>Transfer contado</span>
                <strong>{{ store.money(session.countedTransferAmount || 0) }}</strong>
              </article>
            </section>

            <section class="cash-card movements-card">
              <header>
                <span>Movimientos</span>
                <h2>{{ session.movements.length }} registros</h2>
              </header>

              @for (movement of session.movements; track movement.uuid) {
                <article class="movement-row">
                  <div>
                    <strong>{{ movement.description || movementLabel(movement.type) }}</strong>
                    <small>{{ movementLabel(movement.type) }} · {{ methodLabel(movement.method) }} · {{ movement.occurredAt }}</small>
                    @if (movement.reference) {
                      <small>{{ movement.reference }}</small>
                    }
                  </div>
                  <strong [class.negative]="movement.amount < 0">{{ store.money(movement.amount) }}</strong>
                </article>
              } @empty {
                <ion-text color="medium">Sin movimientos registrados.</ion-text>
              }
            </section>

            @if (session.notes) {
              <section class="cash-message">
                {{ session.notes }}
              </section>
            }
          }
        </ion-content>

        @if (selectedHistorySession(); as session) {
          <ion-footer>
            <ion-toolbar>
              <div class="history-detail-actions">
                <ion-button expand="block" size="large" (click)="printClosing(session)">
                  <ion-icon name="print-outline" slot="start"></ion-icon>
                  Imprimir cierre
                </ion-button>
              </div>
            </ion-toolbar>
          </ion-footer>
        }
      </ng-template>
    </ion-modal>

    @if (printableSession(); as session) {
      <section class="print-closing" aria-hidden="true">
        <article class="closing-paper">
          <header class="closing-print-header">
            <strong>{{ receiptBusinessName() }}</strong>
            <span>Cierre de {{ store.selectedCashRegister()?.name || 'caja' }}</span>
            <span>{{ session.folio }}</span>
          </header>

          <section class="closing-print-block">
            <div>
              <span>Apertura</span>
              <strong>{{ session.openedAt }}</strong>
            </div>
            <div>
              <span>Cierre</span>
              <strong>{{ session.closedAt || 'N/D' }}</strong>
            </div>
            <div>
              <span>Abrió</span>
              <strong>{{ session.openedByUser || 'N/D' }}</strong>
            </div>
            <div>
              <span>Cerró</span>
              <strong>{{ session.closedByUser || 'N/D' }}</strong>
            </div>
          </section>

          <section class="closing-print-totals">
            <h2>Totales</h2>
            <div>
              <span>Efectivo esperado</span>
              <strong>{{ store.money(session.expectedCashAmount) }}</strong>
            </div>
            <div>
              <span>Efectivo contado</span>
              <strong>{{ store.money(session.countedCashAmount || 0) }}</strong>
            </div>
            <div>
              <span>Transfer esperado</span>
              <strong>{{ store.money(session.expectedTransferAmount) }}</strong>
            </div>
            <div>
              <span>Transfer contado</span>
              <strong>{{ store.money(session.countedTransferAmount || 0) }}</strong>
            </div>
            <div>
              <span>Total esperado</span>
              <strong>{{ store.money(session.expectedTotal) }}</strong>
            </div>
            <div>
              <span>Total contado</span>
              <strong>{{ store.money(countedTotal(session)) }}</strong>
            </div>
            <div class="closing-difference">
              <span>Diferencia</span>
              <strong>{{ store.money(session.differenceAmount) }}</strong>
            </div>
          </section>

          <section class="closing-print-movements">
            <h2>Movimientos</h2>
            @for (movement of session.movements; track movement.uuid) {
              <div class="closing-print-row">
                <div>
                  <strong>{{ movement.description || movementLabel(movement.type) }}</strong>
                  <span>{{ movementLabel(movement.type) }} · {{ methodLabel(movement.method) }}</span>
                  <small>{{ movement.occurredAt }}</small>
                  @if (movement.reference) {
                    <small>{{ movement.reference }}</small>
                  }
                </div>
                <strong>{{ store.money(movement.amount) }}</strong>
              </div>
            } @empty {
              <span>Sin movimientos registrados.</span>
            }
          </section>

          @if (session.notes) {
            <section class="closing-print-notes">
              <h2>Notas</h2>
              <span>{{ session.notes }}</span>
            </section>
          }

          <footer class="closing-print-footer">
            <span>Firma responsable</span>
            <strong>________________________</strong>
          </footer>
        </article>
      </section>
    }
  `,
  styles: `
    ion-toolbar {
      --background: var(--pos-color-surface);
    }

    .cash-content {
      --padding-bottom: calc(72px + env(safe-area-inset-bottom));
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .cash-shell {
      display: grid;
      gap: 18px;
      margin: 0 auto;
      max-width: 1120px;
      padding-bottom: 36px;
    }

    .cash-card,
    .summary-grid article {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      box-shadow: 0 10px 28px var(--pos-color-shadow);
    }

    .skeleton-card {
      min-height: 82px;
    }

    .skeleton-row ion-skeleton-text {
      margin-bottom: 8px;
    }

    .cash-card {
      display: grid;
      gap: 14px;
      padding: 18px;
    }

    .cash-card header,
    .open-card header {
      display: grid;
      gap: 4px;
    }

    .cash-card h1,
    .cash-card h2,
    .summary-grid strong {
      margin: 0;
    }

    .cash-card header span,
    .summary-grid span,
    .movement-row small {
      color: var(--pos-color-muted);
    }

    .summary-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .summary-grid article {
      display: grid;
      gap: 6px;
      padding: 16px;
    }

    .summary-grid strong {
      font-size: 22px;
    }

    .cash-message {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      padding: 14px;
    }

    .cash-message.error {
      color: var(--ion-color-danger);
    }

    .movement-row {
      align-items: center;
      border-top: 1px solid var(--pos-color-border);
      display: grid;
      gap: 12px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding-top: 12px;
    }

    .movement-row strong,
    .movement-row small {
      display: block;
    }

    .movement-row .negative {
      color: var(--ion-color-danger);
    }

    .history-list {
      display: grid;
      gap: 12px;
    }

    .history-card {
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      cursor: pointer;
      display: grid;
      gap: 12px;
      padding: 14px;
    }

    .history-card header {
      align-items: start;
      display: flex;
      gap: 12px;
      justify-content: space-between;
    }

    .history-card span,
    .history-card small,
    .history-totals span,
    .closing-detail-hero span,
    .closing-detail-hero small {
      color: var(--pos-color-muted);
    }

    .history-card strong,
    .history-card span,
    .history-card small {
      display: block;
    }

    .history-totals {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .history-totals div {
      background: var(--pos-color-input, rgba(0, 0, 0, 0.04));
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 4px;
      padding: 10px;
    }

    .difference-pill {
      border: 1px solid var(--pos-color-border);
      border-radius: 999px;
      color: var(--pos-color-text) !important;
      font-weight: 800;
      padding: 5px 10px;
      white-space: nowrap;
    }

    .difference-pill.positive {
      border-color: var(--ion-color-success);
      color: var(--ion-color-success) !important;
    }

    .difference-pill.negative {
      border-color: var(--ion-color-danger);
      color: var(--ion-color-danger) !important;
    }

    .closing-detail-hero {
      background: var(--pos-color-input, rgba(0, 0, 0, 0.04));
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 4px;
      margin-bottom: 12px;
      padding: 16px;
    }

    .closing-detail-hero h2 {
      margin: 0;
    }

    .closing-detail-hero strong {
      font-size: 32px;
    }

    .history-detail-actions {
      padding: 8px 12px;
    }

    .print-closing {
      display: none;
    }

    .cash-modal-content {
      --padding-bottom: 18px;
      --padding-end: 18px;
      --padding-start: 18px;
      --padding-top: 18px;
    }

    .closing-summary {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-bottom: 12px;
    }

    .closing-summary article {
      background: var(--pos-color-input, rgba(0, 0, 0, 0.04));
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 4px;
      padding: 14px;
    }

    .closing-summary span {
      color: var(--pos-color-muted);
      font-size: 13px;
    }

    .closing-summary strong {
      font-size: 22px;
    }

    @media (max-width: 760px) {
      .summary-grid,
      .closing-summary,
      .history-totals {
        grid-template-columns: 1fr;
      }
    }

    @media print {
      ion-header,
      ion-content,
      ion-modal,
      ion-footer {
        display: none !important;
      }

      .print-closing {
        background: #fff;
        color: #111;
        display: block !important;
        font-family: Arial, sans-serif;
        inset: 0;
        padding: 0;
        position: fixed;
        z-index: 9999;
      }

      .closing-paper {
        display: grid;
        gap: 12px;
        margin: 0 auto;
        max-width: 380px;
        padding: 12px;
      }

      .closing-print-header,
      .closing-print-footer {
        display: grid;
        gap: 4px;
        text-align: center;
      }

      .closing-print-header strong {
        font-size: 18px;
        text-transform: uppercase;
      }

      .closing-print-header span,
      .closing-print-block span,
      .closing-print-movements span,
      .closing-print-movements small,
      .closing-print-notes span,
      .closing-print-footer span {
        color: #444;
        font-size: 12px;
      }

      .closing-print-block,
      .closing-print-totals,
      .closing-print-movements,
      .closing-print-notes,
      .closing-print-footer {
        border-top: 1px dashed #777;
        display: grid;
        gap: 8px;
        padding-top: 10px;
      }

      .closing-print-block div,
      .closing-print-totals div,
      .closing-print-row {
        align-items: start;
        display: grid;
        gap: 10px;
        grid-template-columns: minmax(0, 1fr) auto;
      }

      .closing-print-block strong,
      .closing-print-totals strong,
      .closing-print-row > strong {
        text-align: right;
      }

      .closing-print-totals h2,
      .closing-print-movements h2,
      .closing-print-notes h2 {
        font-size: 13px;
        margin: 0;
        text-transform: uppercase;
      }

      .closing-print-row {
        border-top: 1px solid #ddd;
        padding-top: 8px;
      }

      .closing-print-row:first-of-type {
        border-top: 0;
        padding-top: 0;
      }

      .closing-print-row strong,
      .closing-print-row span,
      .closing-print-row small {
        display: block;
      }

      .closing-difference {
        border-top: 1px solid #111;
        font-size: 13px;
        padding-top: 8px;
      }

      .closing-print-footer {
        margin-top: 18px;
        padding-top: 24px;
      }
    }
  `,
})
export class CashPageComponent {
  private readonly route = inject(ActivatedRoute);
  readonly store = inject(PosStoreService);
  readonly returnRoute = signal('/pos');
  readonly openingCashAmount = signal(0);
  readonly openingNotes = signal('');
  readonly movementType = signal<'expense' | 'withdrawal' | 'cash_in' | 'adjustment'>('expense');
  readonly movementMethod = signal<'cash' | 'transfer'>('cash');
  readonly movementAmount = signal(0);
  readonly movementDescription = signal('');
  readonly movementReference = signal('');
  readonly countedCashAmount = signal(0);
  readonly countedTransferAmount = signal(0);
  readonly closingNotes = signal('');
  readonly movementOpen = signal(false);
  readonly closingOpen = signal(false);
  readonly historyOpen = signal(false);
  readonly historyDetailOpen = signal(false);
  readonly selectedHistorySession = signal<PosCashSession | null>(null);
  readonly printableSession = signal<PosCashSession | null>(null);
  readonly cashReady = signal(false);

  constructor() {
    const scope = this.route.snapshot.queryParamMap.get('scope') === 'general' ? 'general' : 'pos';
    const fallback = scope === 'general' ? '/repairs' : '/pos';
    this.returnRoute.set(this.safeReturnRoute(this.route.snapshot.queryParamMap.get('return'), fallback));

    void this.store.setCashScope(scope).then(() => {
      this.syncCountedAmounts();
      this.cashReady.set(true);
    });
  }

  async openCash(): Promise<void> {
    await this.store.openCashSession({
      openingCashAmount: this.openingCashAmount(),
      notes: this.openingNotes() || undefined,
    });
    this.openingCashAmount.set(0);
    this.openingNotes.set('');
    this.syncCountedAmounts();
  }

  async addMovement(): Promise<void> {
    try {
      await this.store.addCashMovement({
        type: this.movementType(),
        method: this.movementMethod(),
        amount: this.movementAmount(),
        description: this.movementDescription(),
        reference: this.movementReference() || undefined,
      });
      this.movementAmount.set(0);
      this.movementDescription.set('');
      this.movementReference.set('');
      this.movementOpen.set(false);
      this.syncCountedAmounts();
    } catch {
      // El store expone el error para la UI.
    }
  }

  async closeCash(): Promise<void> {
    try {
      await this.store.closeCashSession({
        countedCashAmount: this.countedCashAmount(),
        countedTransferAmount: this.countedTransferAmount(),
        notes: this.closingNotes() || undefined,
      });
      this.countedCashAmount.set(0);
      this.countedTransferAmount.set(0);
      this.closingNotes.set('');
      this.closingOpen.set(false);
    } catch {
      // El store expone el error para la UI.
    }
  }

  openClosingModal(): void {
    this.syncCountedAmounts();
    this.closingOpen.set(true);
  }

  async openHistoryModal(): Promise<void> {
    this.historyOpen.set(true);
    await this.store.loadCashHistory();
  }

  openHistoryDetail(session: PosCashSession): void {
    this.selectedHistorySession.set(session);
    this.historyDetailOpen.set(true);
  }

  printClosing(session: PosCashSession): void {
    this.printableSession.set(session);

    setTimeout(() => {
      const clearPrintableSession = () => this.printableSession.set(null);

      window.addEventListener('afterprint', clearPrintableSession, { once: true });
      window.print();
    }, 80);
  }

  countedTotal(session: PosCashSession): number {
    return (session.countedCashAmount || 0) + (session.countedTransferAmount || 0);
  }

  receiptBusinessName(): string {
    const workspace = this.store.selectedWorkspace();

    return workspace.posSettings?.posName || workspace.name;
  }

  movementLabel(type: PosCashMovement['type']): string {
    return {
      opening: 'Apertura',
      sale: 'Venta',
      expense: 'Gasto',
      withdrawal: 'Retiro',
      cash_in: 'Entrada',
      adjustment: 'Ajuste',
    }[type];
  }

  methodLabel(method: PosCashMovement['method']): string {
    return method === 'transfer' ? 'Transferencia' : 'Efectivo';
  }

  private syncCountedAmounts(): void {
    const session = this.store.cashSession();

    if (!session) {
      return;
    }

    this.countedCashAmount.set(session.expectedCashAmount);
    this.countedTransferAmount.set(session.expectedTransferAmount);
  }

  private safeReturnRoute(value: string | null, fallback: string): string {
    return value?.startsWith('/') && !value.startsWith('//') ? value : fallback;
  }
}
